import {
  CARD_BORDER_CONTRAST_THRESHOLD,
  CONTROL_HOVER_LIGHTNESS_STEP,
  MIN_TEXT_CONTRAST,
  TINT_RATIO,
  TINT_STRONG_RATIO,
} from './booking-branding.constants';
import type {
  BookingTheme,
  BookingThemeStyle,
} from './booking-theme.interface';
import {
  contrastRatio,
  hexToHsl,
  hslToHex,
  isHexColor,
  mixOverWhite,
} from './color.util';

const WHITE = '#FFFFFF';
const BLACK = '#000000';
const LIGHTNESS_SEARCH_ITERATIONS = 20;

/** `null` (or an unreadable value) means the product's default look. */
export function deriveBookingTheme(accent: string | null): BookingTheme | null {
  if (!accent || !isHexColor(accent)) return null;

  const background = accent.toUpperCase();
  const control = deriveControl(background);
  const whiteOnAccent = contrastRatio(WHITE, background);
  return {
    background,
    onBackground:
      whiteOnAccent >= contrastRatio(BLACK, background) ? WHITE : BLACK,
    control,
    controlHover: darken(control, CONTROL_HOVER_LIGHTNESS_STEP),
    onControl: WHITE,
    tint: mixOverWhite(control, TINT_RATIO),
    tintStrong: mixOverWhite(control, TINT_STRONG_RATIO),
    cardNeedsBorder: whiteOnAccent < CARD_BORDER_CONTRAST_THRESHOLD,
  };
}

export function bookingThemeStyle(
  theme: BookingTheme | null,
): BookingThemeStyle | undefined {
  if (!theme) return undefined;

  const style: BookingThemeStyle = {
    '--bk-bg': theme.background,
    '--bk-on-bg': theme.onBackground,
    '--bk-control': theme.control,
    '--bk-control-hover': theme.controlHover,
    '--bk-on-control': theme.onControl,
    '--bk-tint': theme.tint,
    '--bk-tint-strong': theme.tintStrong,
  };
  if (theme.cardNeedsBorder) {
    style['--bk-card-border'] = `1px solid ${theme.control}`;
  }
  return style;
}

/**
 * Largest HSL lightness of the accent's hue and saturation that passes. `passing`
 * only ever holds a checked lightness, and L = 0 (black) always passes.
 */
function deriveControl(accent: string): string {
  if (isReadableControl(accent)) return accent;

  const { h, s, l } = hexToHsl(accent);
  let passing = 0;
  let failing = l;
  for (let i = 0; i < LIGHTNESS_SEARCH_ITERATIONS; i++) {
    const middle = (passing + failing) / 2;
    if (isReadableControl(hslToHex({ h, s, l: middle }))) passing = middle;
    else failing = middle;
  }
  return hslToHex({ h, s, l: passing });
}

function isReadableControl(color: string): boolean {
  return (
    contrastRatio(WHITE, color) >= MIN_TEXT_CONTRAST &&
    contrastRatio(color, mixOverWhite(color, TINT_RATIO)) >= MIN_TEXT_CONTRAST
  );
}

function darken(color: string, step: number): string {
  const { h, s, l } = hexToHsl(color);
  return hslToHex({ h, s, l: Math.max(0, l - step) });
}
